import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Rating,
  TextField,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  CircularProgress,
  Alert,
  Snackbar,
  Chip
} from '@mui/material';
import { collection, addDoc, query, where, getDocs, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { COLLECTIONS } from '../../firebase/collections';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EngineeringIcon from '@mui/icons-material/Engineering';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const WorkRequestFeedback = ({ workRequest, onFeedbackSubmitted }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [timelinessRating, setTimelinessRating] = useState('');
  const [qualityRating, setQualityRating] = useState('');
  const [communicationRating, setCommunicationRating] = useState('');
  const [loading, setLoading] = useState(false);
  const [existingFeedback, setExistingFeedback] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const { currentUser } = useAuth();

  useEffect(() => {
    const checkExistingFeedback = async () => {
      try {
        if (!workRequest.id || !currentUser) return;

        const q = query(
          collection(db, COLLECTIONS.WORK_REQUEST_FEEDBACK),
          where('workRequestId', '==', workRequest.id),
          where('createdBy', '==', currentUser.uid)
        );

        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          const feedbackData = {
            id: querySnapshot.docs[0].id,
            ...querySnapshot.docs[0].data()
          };
          setExistingFeedback(feedbackData);
          setRating(feedbackData.rating || 0);
          setComment(feedbackData.comment || '');
          setTimelinessRating(feedbackData.details?.timeliness || '');
          setQualityRating(feedbackData.details?.quality || '');
          setCommunicationRating(feedbackData.details?.communication || '');
        }
      } catch (error) {
        console.error('Erreur lors de la vérification du feedback existant:', error);
      }
    };

    checkExistingFeedback();
  }, [workRequest.id, currentUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError('');

      if (!currentUser || !workRequest.id) {
        setError('Informations utilisateur ou demande manquantes');
        setLoading(false);
        return;
      }

      if (rating === 0) {
        setError('Veuillez attribuer une note globale');
        setLoading(false);
        return;
      }

      const feedbackData = {
        workRequestId: workRequest.id,
        rating,
        comment: comment.trim(),
        details: {
          timeliness: timelinessRating,
          quality: qualityRating,
          communication: communicationRating
        },
        createdBy: currentUser.uid,
        createdByName: currentUser.displayName || 'Utilisateur',
        updatedAt: serverTimestamp()
      };

      if (existingFeedback) {
        // Mettre à jour le feedback existant
        await updateDoc(doc(db, COLLECTIONS.WORK_REQUEST_FEEDBACK, existingFeedback.id), feedbackData);
      } else {
        // Créer un nouveau feedback
        feedbackData.createdAt = serverTimestamp();
        await addDoc(collection(db, COLLECTIONS.WORK_REQUEST_FEEDBACK), feedbackData);

        // Mettre à jour la demande pour indiquer qu'un feedback a été donné
        await updateDoc(doc(db, COLLECTIONS.WORK_REQUESTS, workRequest.id), {
          hasFeedback: true,
          updatedAt: serverTimestamp()
        });
      }

      setSuccess(true);
      
      if (onFeedbackSubmitted) {
        onFeedbackSubmitted();
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi du feedback:', error);
      setError('Erreur lors de l\'envoi du feedback');
    } finally {
      setLoading(false);
    }
  };

  const isDisabled = workRequest.status !== 'completed' || loading;

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Évaluation de l'intervention
        {existingFeedback && (
          <Chip 
            label="Déjà évalué" 
            color="success" 
            size="small" 
            icon={<CheckCircleIcon />} 
            sx={{ ml: 2 }} 
          />
        )}
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Paper variant="outlined" sx={{ p: 2 }}>
        {workRequest.status !== 'completed' ? (
          <Alert severity="info">
            L'évaluation sera disponible une fois l'intervention terminée.
          </Alert>
        ) : (
          <form onSubmit={handleSubmit}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Note globale
              </Typography>
              <Rating
                name="overall-rating"
                value={rating}
                onChange={(event, newValue) => {
                  setRating(newValue);
                }}
                size="large"
                disabled={loading}
              />
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Critères spécifiques
              </Typography>
              
              <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
                <FormLabel component="legend" sx={{ display: 'flex', alignItems: 'center' }}>
                  <AccessTimeIcon sx={{ mr: 1 }} /> Respect des délais
                </FormLabel>
                <RadioGroup
                  row
                  name="timeliness-rating"
                  value={timelinessRating}
                  onChange={(e) => setTimelinessRating(e.target.value)}
                >
                  <FormControlLabel value="excellent" control={<Radio />} label="Excellent" disabled={loading} />
                  <FormControlLabel value="good" control={<Radio />} label="Bon" disabled={loading} />
                  <FormControlLabel value="average" control={<Radio />} label="Moyen" disabled={loading} />
                  <FormControlLabel value="poor" control={<Radio />} label="À améliorer" disabled={loading} />
                </RadioGroup>
              </FormControl>
              
              <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
                <FormLabel component="legend" sx={{ display: 'flex', alignItems: 'center' }}>
                  <EngineeringIcon sx={{ mr: 1 }} /> Qualité de l'intervention
                </FormLabel>
                <RadioGroup
                  row
                  name="quality-rating"
                  value={qualityRating}
                  onChange={(e) => setQualityRating(e.target.value)}
                >
                  <FormControlLabel value="excellent" control={<Radio />} label="Excellent" disabled={loading} />
                  <FormControlLabel value="good" control={<Radio />} label="Bon" disabled={loading} />
                  <FormControlLabel value="average" control={<Radio />} label="Moyen" disabled={loading} />
                  <FormControlLabel value="poor" control={<Radio />} label="À améliorer" disabled={loading} />
                </RadioGroup>
              </FormControl>
              
              <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
                <FormLabel component="legend" sx={{ display: 'flex', alignItems: 'center' }}>
                  <ThumbUpIcon sx={{ mr: 1 }} /> Communication
                </FormLabel>
                <RadioGroup
                  row
                  name="communication-rating"
                  value={communicationRating}
                  onChange={(e) => setCommunicationRating(e.target.value)}
                >
                  <FormControlLabel value="excellent" control={<Radio />} label="Excellent" disabled={loading} />
                  <FormControlLabel value="good" control={<Radio />} label="Bon" disabled={loading} />
                  <FormControlLabel value="average" control={<Radio />} label="Moyen" disabled={loading} />
                  <FormControlLabel value="poor" control={<Radio />} label="À améliorer" disabled={loading} />
                </RadioGroup>
              </FormControl>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Commentaires et suggestions
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                placeholder="Partagez votre expérience et vos suggestions d'amélioration..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                disabled={loading}
              />
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={isDisabled}
              >
                {loading ? <CircularProgress size={24} /> : existingFeedback ? 'Mettre à jour' : 'Envoyer'}
              </Button>
            </Box>
          </form>
        )}
      </Paper>
      
      <Snackbar
        open={success}
        autoHideDuration={6000}
        onClose={() => setSuccess(false)}
        message="Merci pour votre évaluation !"
      />
    </Box>
  );
};

export default WorkRequestFeedback;
